import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

// Derives the Project Assistant's working context from the current route:
// page key, project id, customer id, plus live photo-selection events.
const nameCache = {};

export const QUICK_ACTIONS = {
  home: ["create_project", "build_schedule", "add_update", "find_photos"],
  project: ["schedule_work", "share_photos", "add_update", "create_task"],
  photos: ["find_photos", "assign_photos", "share_selected", "create_report"],
  customers: ["create_project", "view_projects", "draft_message"],
  customer: ["create_project_customer", "view_projects", "draft_message"],
  schedule: ["build_schedule", "reschedule", "available_days"],
  financials: ["money_collected", "money_who_owes", "money_losing", "draft_estimate", "find_invoice", "payment_reminder"],
  generic: ["create_project", "find_photos", "build_schedule"],
};

function pageKey(path) {
  if (path === "/app") return "home";
  if (/^\/app\/projects\/[^/]+/.test(path) && !path.startsWith("/app/projects/new")) return "project";
  if (path.startsWith("/app/projects")) return "projects";
  if (path.startsWith("/app/photos")) return "photos";
  if (/^\/app\/customers\/[^/]+/.test(path)) return "customer";
  if (path.startsWith("/app/customers")) return "customers";
  if (path.startsWith("/app/schedule")) return "schedule";
  if (path.startsWith("/app/financials")) return "financials";
  return "generic";
}

export function useAssistantContext() {
  const loc = useLocation();
  const path = loc.pathname;
  const projMatch = path.match(/^\/app\/projects\/([^/]+)/);
  const custMatch = path.match(/^\/app\/customers\/([^/]+)/);
  const rawProjectId = projMatch && projMatch[1] !== "new" ? projMatch[1] : null;
  const customerId = custMatch ? custMatch[1] : null;

  const [label, setLabel] = useState(null);
  const [cleared, setCleared] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);

  useEffect(() => { setCleared(false); }, [path]);

  useEffect(() => {
    const onSel = (e) => setSelectedPhotoIds(e.detail?.photoIds || []);
    window.addEventListener("bf:photo-selection", onSel);
    return () => window.removeEventListener("bf:photo-selection", onSel);
  }, []);

  useEffect(() => {
    let live = true;
    setLabel(null);
    const fetchName = async (kind, id) => {
      const ck = `${kind}:${id}`;
      if (nameCache[ck]) { if (live) setLabel(nameCache[ck]); return; }
      try {
        const { data } = await api.get(`/${kind}s/${id}`);
        const name = data?.name || data?.display_name || null;
        nameCache[ck] = name;
        if (live) setLabel(name);
      } catch (e) { /* context stays generic */ }
    };
    if (rawProjectId) fetchName("project", rawProjectId);
    else if (customerId) fetchName("customer", customerId);
    return () => { live = false; };
  }, [rawProjectId, customerId]);

  return {
    page: pageKey(path),
    path,
    projectId: cleared ? null : rawProjectId,
    customerId,
    label: cleared ? null : label,
    clearContext: () => setCleared(true),
    selectedPhotoIds,
  };
}
